import React, { useState } from "react";
import { Link, DisabledPrompt, BCMSRecord, useGlobalController, useBCMSContext, BCMSDate } from "bcms-react";
import { FormContainer, FormRowView, FormRow } from "components/shared";
import { PrimaryButton, SecondaryButton } from "components/ui";
import { UITextClearInput } from "components/ui/UITextInput";
import Classes, { Account, CheckingAccount } from "model";

import "./styles.css";

/**
 * @function AccountDetailsView
 * @return {React.Component}
 * @param {STRING} accountNickname - Sets the account nickname. 
 * @param {STRING} accountName - Sets the account name.
 * @param {STRING} accountNumber - Sets the account number. 
 * @param {STRING} routingNumber - Sets the routing number. 
 * @param {STRING} dateOpened - Sets the open date. 
 * @param {STRING} interestRate - Sets the interest rate. 
 * @param {STRING} yearToDateInterest - Sets the year-to-date interest. 
 * @param {STRING} lastDepositDate - Sets the last deposit date. 
 * @param {STRING} lastDeposit - Sets the last deposit amount. 
 * @param {STRING} lastInterestPaidDate - Sets the last interest paid date. 
 * @param {STRING} lastInterestAmount - Sets the last interest amount. 
 * @exported true
 * @description The main component for the AccountDetailsView. This component is shared between MobileAccountDetailsModal and AccountDetailsModal.
 */
export default function AccountDetailsView(props) {
    // Initalize globalController and set the 'checkingAccount' variable to 'props.data'
    const globalController = useGlobalController();
    const bcmsContext = useBCMSContext();

    const checkingAccount = props.data;

    // Define the default state and initalize state for the nickname field
    const defaultState = { "show": false, "value": checkingAccount.nickname };
    const [nickname, setNickname] = useState(defaultState);

    // cancel :: set the nickname state back to default
    const cancel = evt => setNickname(defaultState);
    // On save update the Account and CheckingAccount classes and batch
    // changes to global model then update state and close the nickname field view
    const save = evt => {
        const account = Account.initByID(checkingAccount.id);
        const updateNickname = nickname.value !== "" ? nickname.value :  checkingAccount.name;
        
        account.nickname = updateNickname;
        checkingAccount.nickname = updateNickname;

        BCMSRecord.batch([
            Account.update(account)
        ], bcmsContext, Classes);

        BCMSRecord.batch([
            Account.update(account),
            CheckingAccount.update(checkingAccount)
        ], globalController);

        setNickname({ ...nickname, "show": false });
    };

    return (
        <div className="account-details-view">
            <FormContainer>
                <FormRowView text="Account Nickname" className="account-nickname-section">
                    {!nickname.show && <b>{checkingAccount.nickname}</b>}

                    {nickname.show &&
                        <div className="input-container">
                            <UITextClearInput value={nickname.value} onChange={value => setNickname({ ...nickname, "value": value })} />
                            <div className="flex-container flex-horizontal align-center button-container">
                                <SecondaryButton text="Cancel" onClick={cancel} />
                                <PrimaryButton text="Save" onClick={save} />
                            </div>
                        </div>}
                </FormRowView>
                { !nickname.show &&
                <FormRow className="change-account-nickname-link">
                    <Link onClick={() => setNickname({ ...nickname, "show": true })}><span data-point="Nickname">Change Account Nickname</span></Link>
                </FormRow>
                }
                <FormRowView text="Account Name" className="account-name-section">
                    <b>{checkingAccount.name}</b>
                </FormRowView>
                <FormRowView text="Account Number" className="account-number-section">
                    <b>{checkingAccount.number}</b>
                </FormRowView>
                <FormRow className="show-hide-full-account-number-link">
                    <DisabledPrompt>
                        <Link to="/" dataPoint="Show_Full_Account_Number_Link">Show Full Account Number</Link>
                    </DisabledPrompt>
                    {/*<DisabledPrompt>
              <Link to="/">Hide Full Account Number</Link>
            </DisabledPrompt> */}
                </FormRow>
                <FormRowView text="Routing Number" className="routing-number-section">
                    <b>123456789</b>
                </FormRowView>
                <FormRowView text="Date Opened" className="date-opened-section">
                    <b>{checkingAccount.dateOpened.format("m/d/Y")}</b>
                </FormRowView>
                <FormRowView text="Interest Rate" className="interest-rate-section interest-section" data-point="InterestRate">
                    <b>{checkingAccount.interestRate}%</b>
                </FormRowView>
                <FormRowView text="Year To Date Interest" className="ytd-interest-section interest-section">
                    <b>${checkingAccount.ytdInterest}</b>
                </FormRowView>
                <FormRowView text="Last Deposit Date" className="last-deposit-date-section">
                    <b>{BCMSDate.init().offset(-20).format("m/d/Y")}</b>
                </FormRowView>
                <FormRowView text="Last Deposit" className="last-deposit-section">
                    <b>$1,000</b>
                </FormRowView>
                <FormRowView text="Last Interest Paid Date" className="last-interest-paid-date-section interest-section">
                    <b>{BCMSDate.init().offset(-10).format("m/d/Y")}</b>
                </FormRowView>
                <FormRowView text="Last Interest Amount" className="last-interest-amount-section interest-section">
                    <b>$100</b>
                </FormRowView>
            </FormContainer>
        </div>
    )
}
